// frontend/src/pages/LandingPage.jsx
// Public landing page — light/dark aware.

import { useContext } from 'react';
import { Link } from 'react-router-dom';
import { DarkModeContext } from '../App';

export default function LandingPage() {
  const { darkMode, toggleDarkMode } = useContext(DarkModeContext);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <nav className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <span className="text-xl font-bold text-gray-900 dark:text-gray-100">Patent Council</span>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600"
                aria-label="Toggle dark mode"
              >
                {darkMode ? '☀️' : '🌙'}
              </button>
              <Link to="/auth" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 px-3 py-2 rounded-md text-sm font-medium">
                Log in
              </Link>
              <Link to="/auth" className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-md text-sm font-medium">
                Sign up free
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 sm:text-5xl md:text-6xl">
            AI-Powered Patent Drafting
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-gray-500 dark:text-gray-400 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            Multi-agent AI system that drafts, critiques, and refines patent applications through adversarial review
          </p>
          <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
            <Link to="/auth" className="w-full flex items-center justify-center px-8 py-3 text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 md:py-4 md:text-lg md:px-10">
              Get started
            </Link>
          </div>
        </div>

        <div className="mt-24">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-gray-100 mb-12">How It Works</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              { n: 1, title: 'Describe Your Invention', body: 'Provide a description of your invention or upload a structured seed document. Add private context for the inventor agent if needed.' },
              { n: 2, title: 'Convene the Council', body: '15 specialized AI agents collaborate and critique across Writing, Adversarial, and Specialist councils, with human-in-the-loop pauses between rounds.' },
              { n: 3, title: 'Review and File', body: 'Receive a filing recommendation (or NOT VIABLE verdict), the full patent draft, and a comprehensive analysis you can download as Markdown, HTML, or JSON.' }
            ].map(step => (
              <div key={step.n} className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-600 text-white mx-auto text-xl font-bold">{step.n}</div>
                <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-gray-100">{step.title}</h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-400">{step.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-24">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-gray-100 mb-12">The Three Councils</h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <CouncilCard
              title="Writing Council (6 agents)"
              members={[
                'Lex Quill: Legal Strategist',
                'Nick Blueprint: Technical Engineer',
                'Dr. Marie Sparks: Research Scientist',
                'Euler Prime: Mathematical Analyst',
                'Argus: Claims Architect',
                'Mindblower: Inventor & Domain Expert'
              ]}
            />
            <CouncilCard
              title="Adversarial Council (5 agents)"
              members={[
                'Max Loophole: Legal Adversary',
                'Wendy Wrench: Technical Adversary',
                'Dr. Nullius Hypothesis: Scientific Adversary',
                'Count Ample: Mathematical Adversary',
                'Wanda Wayback: Prior Art Hunter'
              ]}
            />
            <CouncilCard
              title="Specialist Agents (4 agents)"
              members={[
                'PATRICIA: Prior Art Research',
                'ARCHIE: Code Analysis',
                'Eligia Prism: Section 101 Eligibility',
                'Vera Accord: IDS & Duty of Candor'
              ]}
            />
          </div>
        </div>

        <div className="mt-24">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-gray-100 mb-12">Filing Recommendations</h2>
          <div className="bg-gray-50 dark:bg-gray-800 p-8 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Verdict</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Meaning</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                {[
                  ['FILE AS-IS', 'text-green-600 dark:text-green-400', 'Zero critical, major-adverse, and major-neutral issues'],
                  ['FIX MAJOR AND RE-REVIEW', 'text-yellow-600 dark:text-yellow-400', 'Zero critical and major-adverse issues, some major-neutral'],
                  ['FIX MAJOR ADVERSE AND RE-REVIEW', 'text-orange-600 dark:text-orange-400', 'Zero critical, but major-adverse issues present'],
                  ['MAJOR REVISION NEEDED', 'text-red-600 dark:text-red-400', 'Critical issues present, substantial revision required'],
                  ['NOT VIABLE', 'text-red-500 dark:text-red-300', 'LLM viability judge concluded one or more objections cannot be designed around']
                ].map(([verdict, cls, meaning]) => (
                  <tr key={verdict}>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${cls}`}>{verdict}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <footer className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-24">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <p className="text-center text-base text-gray-400 dark:text-gray-500">
            © 2025 Patent Council. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function CouncilCard({ title, members }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">{title}</h3>
      <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        {members.map(m => <li key={m}>{m}</li>)}
      </ul>
    </div>
  );
}
